/** VÂNZĂRILE DIN ZONĂ: tipurile și clientul API ale filei «Vânzări în zonă».
 *
 *  Ofertele le culege colectorul de prețuri (`market-price-collector/`) din
 *  anunțurile publice din jurul terenului; control plane-ul doar le dă mai
 *  departe (`control-plane/api/piata.py`), cu punctul terenului luat din actele
 *  proiectului. Pagina cere colectarea, întreabă din când în când cum merge și
 *  arată ce s-a găsit: funcțiile pure de aici fac filtrele, ordinea și
 *  împărțirea pe camere. */
import { useEffect, useRef } from "react";

import { get, post } from "../../api";

export type ClasaFinisaje = "low" | "standard" | "premium" | "luxury";
export type TipFatada = "cortina" | "ventilata" | "placata" | "umeda";

/** Estimarea colectorului, cu frazele din anunț pe care s-a sprijinit. */
export type Clasificare = {
  versiune: number;
  finisaje: { clasa: ClasaFinisaje | null; dovezi: string[]; incredere: "mica" | "medie" | null; din: string };
  fatada: { tip: TipFatada | null; dovezi: string[]; incredere: "mica" | "medie" | null; din: string };
  /** ce spune anunțul despre TVA: `fara` = «+ TVA», prețul e fără el; lipsește
   *  pe clasificările de dinaintea regulii */
  tva?: { pret: "fara" | "cu" | null; dovezi: string[]; din: string };
  /** de ce oferta nu spune cât se vinde un apartament (o chirie pusă la
   *  vânzare, un preț pe m² prea mic); nu intră în eșantion */
  suspect?: MotivSuspect[];
};

export type MotivSuspect = "pret_mp_prea_mic" | "pare_inchiriere";

/** O ofertă din listă: ce trebuie pentru tabel, fără descriere și poze. */
export type Oferta = {
  id: number; sursa: string; id_extern: string; url: string; titlu: string | null;
  pret: number | null; moneda: string | null; pret_mp: number | null; suprafata_mp: number | null;
  camere: number | null; etaj: number | null; etaje_cladire: number | null; an_constructie: number | null;
  piata: "primara" | "secundara" | null; stare_constructie: string | null;
  lat: number | null; lon: number | null; raza_loc_m: number | null;
  loc_id: string | null; cartier: string | null; agentie: string | null;
  clasificare: Clasificare | null; publicat_la: string | null; detalii_la: string | null;
  /** până la pinul anunțului; `distanta_aprox` = până la cartierul lui */
  distanta_km: number; distanta_aprox: boolean;
  /** cât cântărește în profilul zonei (distanța și vechimea) */
  pondere: number;
  /** mai veche decât pragul de vechime: se arată cu avertisment */
  vechi: boolean;
  imagine: string | null; nr_imagini: number;
  /** același apartament, anunțat și de altă agenție sau pe alt portal */
  dubluri?: { sursa: string; url: string; id: number }[];
};

export type Imagine = { mic: string | null; mare: string | null };

/** Tot ce s-a citit despre un anunț (la clic). */
export type Anunt = Omit<Oferta, "distanta_km" | "distanta_aprox" | "pondere" | "vechi" | "imagine" | "nr_imagini"> & {
  adresa: string | null; material: string | null; incalzire: string | null; descriere: string | null;
  imagini: Imagine[]; caracteristici: Record<string, string[]>;
  modificat_la: string | null; prima_vedere: string | null; ultima_vedere: string | null;
};

export type Progres = {
  etapa?: "locatii" | "cautare" | "detalii"; facut?: number; total?: number;
  anunturi?: number; citite?: number; treapta?: number;
};

export type SursaColectare = {
  stare: "ruleaza" | "ok" | "eroare" | "interzis" | "neconfigurat"; motiv?: string;
  /** răspunsurile plătite prin Zyte ale colectării (imobiliare.ro, OLX) */
  cereri_zyte?: number;
  treapta?: { index: number; raza_km: number; ani: number | null; an_minim: number | null };
  numar?: number; largita?: boolean;
  /** treptele urcate, cu câte oferte cu preț a dat fiecare */
  trepte?: { raza_km: number; ani: number | null; numar: number }[];
  /** de ce s-a oprit urcarea: eșantionul e destul de mare, treapta următoare
   *  ar fi ieșit din limite, sau nu mai era nicio treaptă */
  oprit?: "tinta" | "limite" | "ultima" | null;
};

export type StareColectare = "in_asteptare" | "ruleaza" | "gata" | "eroare";

/** Cât mai ține o colectare deschisă (`citire.estimarea` în colector): paginile de
 *  anunț rămase, cu pauza fiecărui site; `completa = false` cât o sursă n-a ajuns
 *  la ele (câte are se află abia după căutarea cartierelor). */
export type Estimare = { secunde: number; completa: boolean };

export type Colectare = {
  id: number; stare: StareColectare; cerut_la: string | null; pornit_la: string | null;
  terminat_la: string | null; forteaza: boolean; eroare: string | null;
  progres: Record<string, Progres>; surse: Record<string, SursaColectare>;
  estimare?: Estimare | null;
};

export type Setari = {
  rol: "principal" | "secundar"; cache_zile: number; trepte: { raza_km: number; ani: number | null }[];
  /** sub minim zona se lărgește oricum; spre țintă, numai cât rămâne în `tinta_km` și `tinta_ani` */
  min_oferte: number; tinta_oferte: number; tinta_km: number; tinta_ani: number;
  vechime_ani: number; pondere_km: number; pondere_ani: number;
};

export type Piata = { colectare: Colectare | null; afisata: Colectare | null; oferte: Oferta[]; setari: Setari };

const cale = (projectId: string) => `/api/projects/${projectId}/piata`;

/** `oferte = false`: numai starea colectării — întrebarea de la câteva secunde
 *  cât se caută; lista unui cartier dens are mii de oferte. */
export const getPiata = (projectId: string, oferte = true) =>
  get<Piata>(oferte ? cale(projectId) : `${cale(projectId)}?oferte=0`, { silent: true });

/** Fără `forteaza`, o zonă căutată de curând se ia din cache (`fel: "din_cache"`).
 *  `silent`: cererea pleacă din fundal, fără un om care să citească o eroare. */
export const colecteaza = (projectId: string, forteaza = false, silent = false) =>
  post<{ colectare: Colectare; fel: "noua" | "in_lucru" | "din_cache" }>(`${cale(projectId)}/colecteaza`, { forteaza },
                                                                       silent ? { silent } : undefined);

/** A trecut citirea actelor de la «rulează» la «gata» între două priviri? Un job
 *  văzut prima dată deja gata e al unei pagini deschise după citire, nu o adresă
 *  care tocmai a venit. */
export const citireaTocmaiGata = (inainte: string | undefined, acum: string) =>
  inainte !== undefined && inainte !== "succeeded" && acum === "succeeded";

/** CĂUTAREA LA ADRESA NOUĂ. Vânzările din zonă se caută numai la cerere
 *  (`colecteaza`); interfața o cere când actele dau terenului o adresă, adică la
 *  capătul citirii lor, ca rezultatele să fie gata până la pagina de brief. Se
 *  uită la joburile pe care centrul de activitate le cere oricum, pe orice pagină
 *  de proiect. Un teren fără adresă primește 422, iar unul căutat în ultimele 7
 *  zile, rezultatele de atunci: amândouă fără nimic de spus omului. */
export function useCautaLaCapatulCitirii(projectId: string, jobs: { id: string; kind: string; status: string }[]) {
  const vazute = useRef(new Map<string, string>());
  useEffect(() => {
    for (const j of jobs) {
      if (j.kind !== "extraction") continue;
      const inainte = vazute.current.get(j.id);
      vazute.current.set(j.id, j.status);
      if (citireaTocmaiGata(inainte, j.status)) void colecteaza(projectId, false, true).catch(() => undefined);
    }
  }, [projectId, jobs]);
}

export const getAnunt = (projectId: string, id: number) => get<Anunt>(`${cale(projectId)}/anunturi/${id}`);

// --- profilul zonei ------------------------------------------------------------

/** O parte a eșantionului: câte oferte, ce parte din pondere (0…1), la ce preț
 *  ponderat. */
export type Parte = { n: number; pct: number; pret_mp: number | null };

/** €/m²: media ponderată, apoi mediana și plaja p10–p90 a ofertelor, neponderate. */
export type PlajaPret = { ponderat: number; mediana: number; p10: number; p90: number };

/** Configurația recomandată; fiecare parte cu cifrele pe care stă. */
export type Recomandare = {
  /** procente întregi care fac 100 */
  mix: { grupa: Exclude<GrupaCamere, "?">; pct: number; n: number; suprafata: number | null; pret_mp: number | null }[];
  mix_n: number;
  /** clasa cu cea mai mare pondere între ofertele cu finisajul estimat; `dif` =
   *  cât cer celelalte clase în plus (sau în minus), ca fracție */
  finisaje: (Parte & { clasa: ClasaFinisaje; n_estimate: number;
                       fata_de: { clasa: ClasaFinisaje; dif: number }[] }) | null;
  /** prețul pentru business case: media ponderată a clasei recomandate (sau a
   *  tuturor, fără clasă), cerut și net de TVA; `fara_tva` = câte scriu «+ TVA» */
  pret: { cerut_mp: number; net_mp: number; cota_tva: number; n: number; fara_tva: number;
          baza: "finisaj" | "toate" };
};

/** Profilul zonei (`market-price-collector/profil.py`), pe aceleași oferte ca
 *  fila; ofertele fără pin stau deoparte, în `aproximative`. */
export type Profil = {
  colectare: { id: number; stare: StareColectare; terminat_la: string | null } | null;
  esantion: { n: number; pondere: number; vechi: number };
  excluse: { suspect: number; ansamblu: number; fara_pret: number; alta_moneda: number };
  pret_mp: PlajaPret | null;
  camere: (Parte & { grupa: GrupaCamere; suprafata: number | null })[];
  finisaje: { impartire: (Parte & { clasa: ClasaFinisaje })[]; necunoscute: number };
  fatade: { impartire: (Parte & { tip: TipFatada })[]; necunoscute: number };
  piete: (Parte & { piata: "primara" | "secundara"; mediana: number })[];
  piata_necunoscuta: number;
  tva: { fara: number; cu: number; nespus: number };
  inele: { de_la: number; pana_la: number; n: number; pret_mp: number }[];
  aproximative: { n: number; pret_mp: PlajaPret | null };
  recomandare: Recomandare | null;
  /** de ce nu e recomandare: niciun eșantion, sau sub `setari.min_oferte` */
  motiv: "putine" | "fara_oferte" | null;
  setari: { pondere_km: number; pondere_ani: number; cota_tva: number; min_oferte: number };
};

export const getProfil = (projectId: string) => get<Profil>(`${cale(projectId)}/profil`, { silent: true });

export const inLucru = (c: Pick<Colectare, "stare"> | null | undefined) => c?.stare === "in_asteptare" || c?.stare === "ruleaza";

/** Numele unei surse, cum îl citește omul. */
export const NUME_SURSA: Record<string, string> = { storia: "Storia", imobiliare: "imobiliare.ro", olx: "OLX" };
export const numeSursa = (cod: string) => NUME_SURSA[cod] ?? cod;

// --- camerele ---------------------------------------------------------------

export type GrupaCamere = "1" | "2" | "3" | "4" | "?";
export const GRUPE: readonly GrupaCamere[] = ["1", "2", "3", "4", "?"];

/** Garsoniera, 2, 3, iar 4 și peste împreună: peste 4 camere sunt prea puține
 *  ca să fie o grupă a lor într-un cartier. */
export function grupa(camere: number | null): GrupaCamere {
  if (camere == null || camere < 1) return "?";
  return camere >= 4 ? "4" : (String(camere) as GrupaCamere);
}

/** Câte oferte are fiecare grupă și ce parte din total (grupele goale lipsesc). */
export function distributie(oferte: Oferta[]): { grupa: GrupaCamere; n: number; pct: number }[] {
  const n: Record<string, number> = {};
  for (const o of oferte) n[grupa(o.camere)] = (n[grupa(o.camere)] ?? 0) + 1;
  return GRUPE.filter((g) => n[g]).map((g) => ({ grupa: g, n: n[g], pct: n[g] / oferte.length }));
}

// --- filtrele și ordinea -----------------------------------------------------

export type Sortare = "distanta" | "pret_mp" | "pret" | "suprafata" | "an" | "pondere";
export const SORTARI: readonly Sortare[] = ["distanta", "pondere", "pret_mp", "pret", "suprafata", "an"];

export function filtreaza(oferte: Oferta[],
                          f: { grupa: GrupaCamere | null; siVechi: boolean; sursa?: string | null }): Oferta[] {
  return oferte.filter((o) => (f.grupa == null || grupa(o.camere) === f.grupa) && (f.siVechi || !o.vechi)
    && (f.sursa == null || o.sursa === f.sursa));
}

/** Câte oferte are fiecare sursă, în ordinea în care apar. */
export function pesurse(oferte: Oferta[]): { sursa: string; n: number }[] {
  const n = new Map<string, number>();
  for (const o of oferte) n.set(o.sursa, (n.get(o.sursa) ?? 0) + 1);
  return [...n].map(([sursa, k]) => ({ sursa, n: k }));
}

/** Distanța și ponderea crescător/descrescător cum se citesc (cea mai apropiată
 *  și cea mai grea întâi); prețurile și suprafața crescător; anul, cel mai nou
 *  întâi. Ce nu se știe merge la capăt, oricare ar fi ordinea. */
export function sorteaza(oferte: Oferta[], s: Sortare): Oferta[] {
  const cheie: Record<Sortare, (o: Oferta) => number | null> = {
    distanta: (o) => o.distanta_km, pondere: (o) => -o.pondere, pret_mp: (o) => o.pret_mp,
    pret: (o) => o.pret, suprafata: (o) => o.suprafata_mp,
    an: (o) => (o.an_constructie == null ? null : -o.an_constructie),
  };
  const k = cheie[s];
  return [...oferte].sort((a, b) => {
    const x = k(a), y = k(b);
    if (x == null || y == null) return x == null ? (y == null ? 0 : 1) : -1;
    return x - y || a.distanta_km - b.distanta_km;
  });
}

/** Până când datele colectării sunt «proaspete» (cache-ul colectorului). */
export function proaspatPanaLa(c: Colectare | null, zile: number): Date | null {
  if (!c?.terminat_la || c.stare !== "gata") return null;
  return new Date(new Date(c.terminat_la).getTime() + zile * 86_400_000);
}
